import {
  Challenge,
  ChallengeType,
  PdfData,
  RemainingChallenges,
  Score,
  TeamReference,
} from "@/models/pdfData";
import { otherTeam } from "@/pdf/utils";

export const sortChallenges = (
  challenges: Challenge[],
  order: "ascending" | "descending" = "descending",
): Challenge[] => {
  const sumOfScore = (score: Score): number => score.team2 + score.team1;
  const orderMultiplyer = order === "descending" ? 1 : -1;
  return challenges.sort((c1, c2) => {
    const setDif = c1.set - c2.set;
    if (setDif) {
      return orderMultiplyer * setDif;
    } else {
      const pointDif =
        sumOfScore(c1.scoreRallyStart) - sumOfScore(c2.scoreRallyStart);
      if (pointDif) {
        return orderMultiplyer * pointDif;
      } else {
        return orderMultiplyer * c2.team.localeCompare(c1.team);
      }
    }
  });
};

export const remainingChallenges = (
  game: PdfData,
  sets: number[] = Array.from({ length: 5 }, (_, i) => i + 1),
): RemainingChallenges =>
  sets
    .map((s) => {
      if (game) {
        return {
          [s]: {
            team1:
              2 -
              game.challenges.filter(
                (c) => c.set === s && countsAsUsedChallengeForTeam("team1", c),
              ).length,
            team2:
              2 -
              game.challenges.filter(
                (c) => c.set === s && countsAsUsedChallengeForTeam("team2", c),
              ).length,
          },
        };
      } else {
        return { [s]: { team1: 2, team2: 2 } };
      }
    })
    .reduce((prev, curr) => ({ ...prev, ...curr }), {});

export const countsAsUsedChallengeForTeam = (
  team: TeamReference,
  challenge: Challenge,
): boolean => {
  const takenByTeam = challenge.team === team;
  const challengeNotDeleted = !challenge.id.startsWith("deleted");
  const challengeNotSuccessful = challenge.challengeResult === otherTeam(team);
  const interruptingChallenge = !challenge.scoreRallyEnd;
  const challengeNotResolvable = challenge.challengeResult === "unresolvable";

  return (
    takenByTeam &&
    challengeNotDeleted &&
    (challengeNotSuccessful ||
      (interruptingChallenge && challengeNotResolvable))
  );
};

export const challengeComplete = (
  challenge: Challenge | undefined,
): boolean => {
  if (!challenge) {
    return false;
  } else {
    const {
      challengeResult,
      set,
      typeOfChallenge,
      team,
      scoreAfterChallenge,
      scoreRallyStart,
      scoreRallyEnd,
    } = challenge;

    const mandatoryFields = [
      challengeResult,
      set,
      typeOfChallenge,
      team,
      scoreAfterChallenge,
    ];

    const allMandatoryFieldsExist = mandatoryFields
      .map((field) => !!field)
      .every(Boolean);
    const oneOfScoresExists = !!(scoreRallyStart || scoreRallyEnd);

    return allMandatoryFieldsExist && oneOfScoresExists;
  }
};

export const getChallengeTypeTranslation = (
  challengeType: ChallengeType,
  onUnknownReturnOriginal = false,
): string => {
  switch (challengeType) {
    case "antenna":
      return "Antenna Touch";
    case "in-out":
      return "Ball In / Out";
    case "blocktouch":
      return "Block Touch";
    case "nettouch":
      return "Net Fault";
    case "floor-touch":
      return "Floor Touch";
    case "foot-fault":
      return "Foot Fault";
    case "last-touch":
      return "Last Touch";
    case "no-nettouch":
      return "No Net Fault";
    case "no-floor-touch":
      return "No Floor Touch";
    case "no-foot-fault":
      return "No Foot Fault";
    case "no-antenna":
      return "No Antenna Touch";
    default:
      if (onUnknownReturnOriginal) {
        return challengeType;
      } else {
        return `Veraltet (war: ${challengeType})`;
      }
  }
};
